import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import styled from 'styled-components';
import { FiArrowLeft, FiHome, FiMessageCircle, FiSend } from 'react-icons/fi';
import { apiRequest } from '../../services/api';
import {
  getConversationMessages,
  getConversations,
  peekConversationMessages,
} from '../../services/conversationsService';
import { useAuth } from '../../auth/AuthContext';

export default function Mensajes() {
  const { id } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { user } = useAuth();
  const bottomRef = useRef(null);
  const messageListRef = useRef(null);
  const activeConversationRef = useRef(id || null);
  const messageCacheRef = useRef(new Map());
  const messageRequestRef = useRef(0);
  const composerRef = useRef(null);
  const [conversations, setConversations] = useState([]);
  const [messages, setMessages] = useState([]);
  const [activeId, setActiveId] = useState(id || null);
  const [text, setText] = useState(location.state?.defaultMessage || (location.state?.intent === 'visit' ? 'Hola, me gustaría coordinar una visita.' : ''));
  const [loading, setLoading] = useState(true);
  const [chatLoading, setChatLoading] = useState(false);
  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const loadConversations = useCallback(async (force = false) => {
    const data = await getConversations({ force });
    setConversations(Array.isArray(data) ? data : []);
    return data;
  }, []);

  useEffect(() => {
    let active = true;
    const initialize = async () => {
      try {
        let conversationId = id;
        if ((location.state?.propertyId || location.state?.projectId) && !id) {
          const conversation = await apiRequest('/conversations', {
            method: 'POST',
            body: {
              propertyId: location.state.propertyId,
              projectId: location.state.projectId,
              vacationRequest: location.state.vacationRequest,
              source: location.state.source,
            },
          });
          conversationId = conversation._id;
          if (active) {
            setActiveId(conversationId);
            navigate(`/mensajes/${conversationId}`, { replace: true, state: location.state });
          }
        }
        const list = await loadConversations();
        if (active && conversationId) {
          activeConversationRef.current = conversationId;
          setActiveId(conversationId);
        } else if (active && list.length) {
          activeConversationRef.current = list[0]._id;
          setActiveId(list[0]._id);
        }
      } catch (requestError) {
        if (active) setError(requestError.message);
      } finally {
        if (active) setLoading(false);
      }
    };
    initialize();
    return () => { active = false; };
  }, [id, location.state, navigate, loadConversations]);

  const loadMessages = useCallback(async () => {
    if (!activeId) return;
    const conversationId = activeId;
    const requestId = ++messageRequestRef.current;
    const cachedMessages = messageCacheRef.current.get(conversationId)
      || peekConversationMessages(conversationId);
    if (cachedMessages) setMessages(cachedMessages);
    setChatLoading(!cachedMessages);
    try {
      const data = await getConversationMessages(conversationId);
      if (requestId !== messageRequestRef.current || activeConversationRef.current !== conversationId) return;
      const nextMessages = Array.isArray(data) ? data : [];
      messageCacheRef.current.set(conversationId, nextMessages);
      setMessages(nextMessages);
      setError('');
      loadConversations(true).catch(() => {});
    } catch (requestError) {
      if (requestId === messageRequestRef.current && activeConversationRef.current === conversationId) {
        setError(requestError.message);
      }
    }
    finally {
      if (requestId === messageRequestRef.current && activeConversationRef.current === conversationId) {
        setChatLoading(false);
      }
    }
  }, [activeId, loadConversations]);

  useEffect(() => {
    loadMessages();
    const timer = window.setInterval(loadMessages, 10000);
    return () => window.clearInterval(timer);
  }, [loadMessages]);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      const messageList = messageListRef.current;
      if (messageList) messageList.scrollTop = messageList.scrollHeight;
    });
    return () => window.cancelAnimationFrame(frame);
  }, [messages, activeId]);

  const activeConversation = conversations.find((conversation) => conversation._id === activeId);
  const activePublication = activeConversation?.property || activeConversation?.project;
  const activePublicationUrl = activeConversation?.project
    ? `/desarrollo/${activeConversation.project.slug || activeConversation.project._id}`
    : `/properties/${activeConversation?.property?._id}`;
  const activeImage = activeConversation?.property?.images?.[0]
    || activeConversation?.project?.media?.find(item => ['PHOTO', 'RENDER'].includes(item.kind))?.url;
  const isVacationConversation = activeConversation?.property?.operation === 'alquiler_vacacional';
  const otherUser = useMemo(() => {
    if (!activeConversation) return null;
    return String(activeConversation.buyer?._id) === String(user?._id)
      ? activeConversation.seller : activeConversation.buyer;
  }, [activeConversation, user]);

  const selectConversation = (conversationId) => {
    messageRequestRef.current += 1;
    activeConversationRef.current = conversationId;
    setActiveId(conversationId);
    const cachedMessages = messageCacheRef.current.get(conversationId)
      || peekConversationMessages(conversationId);
    setMessages(cachedMessages || []);
    setChatLoading(!cachedMessages);
    setError('');
    navigate(`/mensajes/${conversationId}`, { replace: true });
  };

  const send = async () => {
    const cleanText = text.trim();
    if (!cleanText || !activeId || sending) return;
    try {
      setSending(true);
      const message = await apiRequest(`/conversations/${activeId}/messages`, { method: 'POST', body: { text: cleanText } });
      setMessages((current) => {
        const nextMessages = [...current, message];
        messageCacheRef.current.set(activeId, nextMessages);
        return nextMessages;
      });
      setText('');
      if (composerRef.current) composerRef.current.style.height = '50px';
      await loadConversations(true);
    } catch (requestError) { setError(requestError.message); }
    finally { setSending(false); }
  };
  const completeStay = async () => {
    try {
      const result = await apiRequest(`/conversations/${activeId}/complete-stay`, { method: 'POST' });
      setError('');
      await loadConversations();
      window.alert(result.message);
    } catch (requestError) { setError(requestError.message); }
  };

  return (
    <Page>
      <Shell>
        <Sidebar $hidden={Boolean(activeId)}>
          <SidebarHeader><div><Kicker>CONVERSACIONES</Kicker><h1>Mensajes</h1></div><span>{conversations.length}</span></SidebarHeader>
          {loading && <State>Preparando tus conversaciones…</State>}
          {!loading && !conversations.length && <State><FiMessageCircle /><strong>Todavía no hay mensajes</strong><p>Cuando consultes una propiedad, la conversación aparecerá acá.</p></State>}
          {conversations.map((conversation) => {
            const publication = conversation.property || conversation.project;
            const publicationUrl = conversation.project
              ? `/desarrollo/${conversation.project.slug || conversation.project._id}`
              : `/properties/${conversation.property?._id}`;
            const image = conversation.property?.images?.[0]
              || conversation.project?.media?.find(item => ['PHOTO', 'RENDER'].includes(item.kind))?.url;
            const peer = String(conversation.buyer?._id) === String(user?._id) ? conversation.seller : conversation.buyer;
            const unread = conversation.unreadBy?.some((value) => String(value?._id || value) === String(user?._id));
            return <ConversationButton type="button" key={conversation._id} $active={conversation._id === activeId} onClick={() => selectConversation(conversation._id)}>
              <Thumb
                role="link"
                tabIndex={0}
                title="Ver publicación"
                onClick={(event) => { event.stopPropagation(); navigate(publicationUrl); }}
                onKeyDown={(event) => { if (event.key === 'Enter') navigate(publicationUrl); }}
              >{image ? <img src={image} alt="" /> : <FiHome />}</Thumb>
              <div><strong>{publication?.title || 'Publicación'}</strong><Reference>{publication?.reference || 'Referencia pendiente'}</Reference><span>{peer?.name || 'Usuario'} {peer?.lastName || ''}</span><small>{conversation.lastMessage || 'Conversación iniciada'}</small></div>
              {unread && <Unread />}
            </ConversationButton>;
          })}
        </Sidebar>

        <Chat $visible={Boolean(activeId)}>
          {activeConversation ? <>
            <ChatHeader>
              <Back type="button" aria-label="Volver a conversaciones" onClick={() => { setActiveId(null); navigate('/mensajes', { replace: true }); }}><FiArrowLeft /></Back>
              <PublicationThumb to={activePublicationUrl} title="Abrir publicación">
                {activeImage ? <img src={activeImage} alt={activePublication?.title || 'Publicación'} /> : <FiHome />}
              </PublicationThumb>
              <div>
                <strong>{activePublication?.title}</strong>
                <Reference>{activePublication?.reference}</Reference>
                <span>{otherUser?.name || 'Usuario'} {otherUser?.lastName || ''}</span>
              </div>
            </ChatHeader>
            <MessageList ref={messageListRef}>
              {chatLoading && !messages.length && <ChatLoading><i /><span>Abriendo conversación…</span></ChatLoading>}
              {isVacationConversation && (
                <ProtectionNotice>
                  <strong>Consulta de alojamiento</strong>
                  <span>NIDO facilita el contacto, pero no confirma reservas ni recibe pagos por la estadía. Acordá condiciones directamente con el anunciante.</span>
                  {String(activeConversation.seller?._id) === String(user?._id)
                    && activeConversation.vacationRequest?.status === 'INQUIRY'
                    && activeConversation.vacationRequest?.checkOut
                    && new Date(activeConversation.vacationRequest.checkOut) <= new Date()
                    && <button type="button" onClick={completeStay}>Confirmar estadía realizada</button>}
                  {activeConversation.vacationRequest?.status === 'COMPLETED' && <em>Estadía verificada · La opinión del huésped está habilitada.</em>}
                </ProtectionNotice>
              )}
              {messages.map((message) => {
                const mine = String(message.sender?._id || message.sender) === String(user?._id);
                return <Bubble key={message._id} $mine={mine}><p>{message.text}</p><time>{new Date(message.createdAt).toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' })}</time></Bubble>;
              })}
              <div ref={bottomRef} />
            </MessageList>
            {error && <Error>{error}</Error>}
            <Composer>
              <textarea
                ref={composerRef}
                rows="1"
                value={text}
                maxLength={2000}
                placeholder="Escribí un mensaje…"
                aria-label="Mensaje"
                onChange={(event) => {
                  setText(event.target.value);
                  event.target.style.height = 'auto';
                  event.target.style.height = `${Math.min(event.target.scrollHeight, 120)}px`;
                }}
                onKeyDown={(event) => { if (event.key === 'Enter' && !event.shiftKey) { event.preventDefault(); send(); } }}
              />
              <Send type="button" onClick={send} disabled={!text.trim() || sending} aria-label={sending ? 'Enviando mensaje' : 'Enviar mensaje'}>
                <FiSend /> <span>{sending ? 'Enviando…' : 'Enviar'}</span>
              </Send>
            </Composer>
          </> : <State><FiMessageCircle /><strong>Elegí una conversación</strong></State>}
        </Chat>
      </Shell>
    </Page>
  );
}

const Page = styled.div`min-height:calc(100vh - 96px);padding:34px clamp(14px,3vw,42px) 70px;background:#f7f5ef;color:#29474f;@media(max-width:760px){width:100%;height:calc(100dvh - 82px);min-height:0;padding:0;overflow:hidden;}`;
const Shell = styled.div`display:grid;width:100%;max-width:1380px;height:min(760px,calc(100dvh - 165px));margin:auto;grid-template-columns:380px minmax(0,1fr);background:#fcfbf8;border:1px solid rgba(42,64,70,.13);box-shadow:0 24px 70px rgba(42,64,70,.08);overflow:hidden;@media(max-width:760px){height:100%;min-height:0;grid-template-columns:minmax(0,1fr);border:0;box-shadow:none;}`;
const Sidebar = styled.aside`min-width:0;overflow-y:auto;border-right:1px solid rgba(42,64,70,.11);@media(max-width:760px){display:${({$hidden})=>$hidden?'none':'block'};width:100%;border:0;overscroll-behavior:contain;}`;
const SidebarHeader = styled.header`display:flex;align-items:flex-end;justify-content:space-between;padding:28px 24px 22px;border-bottom:1px solid rgba(42,64,70,.1);h1{margin:0;font-size:30px;font-weight:550;letter-spacing:-.04em;}span{color:#b45f40;font-size:13px;}@media(max-width:760px){padding:22px 16px 18px;h1{font-size:28px;}}`;
const Kicker = styled.div`margin-bottom:5px;color:#b45f40;font-size:12px;font-weight:700;letter-spacing:.16em;`;
const Reference = styled.em`display:block;margin:3px 0;color:#b45f40;font-size:12px;font-style:normal;font-weight:700;letter-spacing:.08em;text-transform:uppercase;`;
const ConversationButton = styled.button`display:grid;width:100%;min-width:0;grid-template-columns:54px minmax(0,1fr) 8px;align-items:center;gap:13px;padding:16px 18px;color:#29474f;background:${({$active})=>$active?'#eef1ed':'transparent'};border:0;border-bottom:1px solid rgba(42,64,70,.08);text-align:left;cursor:pointer;>div{min-width:0;overflow:hidden;}div strong,div em,div span,div small{display:block;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}strong{font-size:14px;}span{margin:3px 0;color:#68777b;font-size:12px;}small{color:#96a09f;font-size:12px;}@media(max-width:760px){grid-template-columns:48px minmax(0,1fr) 8px;gap:11px;padding:14px 13px;}`;
const Thumb = styled.div`display:grid;width:52px;height:52px;place-items:center;overflow:hidden;background:#e7ebe7;color:#b45f40;border-radius:50%;cursor:pointer;img{width:100%;height:100%;object-fit:cover;}&:focus-visible{outline:3px solid rgba(180,95,64,.3);outline-offset:3px;}`;
const PublicationThumb = styled(Link)`display:grid;width:52px;height:52px;flex:0 0 52px;place-items:center;overflow:hidden;background:#e7ebe7;color:#b45f40;border-radius:50%;text-decoration:none;transition:transform .2s,box-shadow .2s;img{width:100%;height:100%;object-fit:cover;}&:hover{transform:translateY(-1px);box-shadow:0 7px 18px rgba(42,64,70,.18);}&:focus-visible{outline:3px solid rgba(180,95,64,.3);outline-offset:3px;}@media(max-width:420px){width:44px;height:44px;flex-basis:44px;}`;
const Unread = styled.i`width:8px;height:8px;background:#b45f40;border-radius:50%;`;
const Chat = styled.section`display:flex;width:100%;min-width:0;min-height:0;overflow:hidden;flex-direction:column;@media(max-width:760px){display:${({$visible})=>$visible?'flex':'none'};}`;
const ChatHeader = styled.header`display:flex;min-width:0;min-height:82px;align-items:center;gap:13px;padding:14px 22px;border-bottom:1px solid rgba(42,64,70,.1);>div:last-child{min-width:0;overflow:hidden;}strong,em,span{display:block;max-width:100%;overflow:hidden;text-overflow:ellipsis;white-space:nowrap;}strong{font-size:15px;}span{margin-top:3px;color:#778587;font-size:12px;}@media(max-width:760px){min-height:68px;padding:8px 10px;gap:7px;}`;
const Back = styled.button`display:none;background:transparent;border:0;color:#29474f;font-size:20px;@media(max-width:760px){display:grid;width:44px;height:44px;flex:0 0 44px;place-items:center;padding:0;border-radius:50%;}`;
const MessageList = styled.div`display:flex;flex:1;min-width:0;min-height:0;overflow-y:auto;overscroll-behavior:contain;flex-direction:column;gap:10px;padding:26px;background:#f3f1eb;@media(max-width:760px){padding:18px 12px;}`;
const ChatLoading = styled.div`display:flex;align-self:center;align-items:center;gap:9px;margin:auto;color:#7d8a8c;font-size:12px;i{width:16px;height:16px;border:2px solid rgba(42,64,70,.16);border-top-color:#b45f40;border-radius:50%;animation:chat-spin .7s linear infinite;}@keyframes chat-spin{to{transform:rotate(360deg)}}`;
const Bubble = styled.div`max-width:min(70%,560px);align-self:${({$mine})=>$mine?'flex-end':'flex-start'};padding:11px 14px;color:${({$mine})=>$mine?'#fff':'#29474f'};background:${({$mine})=>$mine?'#29474f':'#fff'};border-radius:${({$mine})=>$mine?'16px 4px 16px 16px':'4px 16px 16px'};box-shadow:0 5px 18px rgba(42,64,70,.06);overflow-wrap:anywhere;word-break:break-word;p{max-width:100%;margin:0;line-height:1.5;white-space:pre-wrap;}time{display:block;margin-top:5px;opacity:.62;font-size:12px;text-align:right;}@media(max-width:760px){max-width:90%;font-size:14px;}`;
const Composer = styled.div`display:grid;width:100%;min-width:0;grid-template-columns:minmax(0,1fr) auto;align-items:end;gap:12px;padding:14px 18px calc(14px + env(safe-area-inset-bottom));border-top:1px solid rgba(42,64,70,.1);background:rgba(252,251,248,.96);backdrop-filter:blur(16px);box-sizing:border-box;textarea{display:block;width:100%;min-width:0;height:50px;min-height:50px;max-height:120px;resize:none;box-sizing:border-box;padding:14px 16px;color:#29474f;background:#f5f3ed;border:1px solid rgba(42,64,70,.14);border-radius:18px;font:inherit;font-size:15px;line-height:21px;outline:none;overflow-y:auto;transition:border-color .2s,box-shadow .2s;&:focus{border-color:rgba(180,95,64,.65);box-shadow:0 0 0 4px rgba(180,95,64,.09);}&::placeholder{color:#9ba4a3;}}@media(max-width:760px){gap:8px;padding:9px 10px calc(9px + env(safe-area-inset-bottom));textarea{padding:13px 14px;font-size:16px;}}`;
const Send = styled.button`display:flex;min-width:112px;height:50px;flex:0 0 auto;align-items:center;justify-content:center;gap:8px;padding:0 20px;color:#fff;background:#b45f40;border:0;border-radius:18px;box-shadow:0 10px 22px rgba(180,95,64,.2);font:inherit;font-size:14px;font-weight:750;cursor:pointer;transition:.2s;&:hover:not(:disabled){background:#a95336;transform:translateY(-1px);}&:disabled{opacity:.42;box-shadow:none;}@media(max-width:520px){width:50px;min-width:50px;padding:0;border-radius:17px;span{display:none;}svg{font-size:19px;}}`;
const State = styled.div`display:grid;min-height:220px;place-content:center;justify-items:center;gap:8px;padding:28px;color:#7d8a8c;text-align:center;svg{font-size:30px;color:#b45f40;}p{max-width:260px;margin:0;font-size:13px;line-height:1.5;}`;
const Error = styled.div`padding:8px 18px;color:#9d3b2c;background:#fff1ed;font-size:12px;overflow-wrap:anywhere;`;
const ProtectionNotice = styled.div`
align-self:center;width:100%;max-width:580px;padding:13px 16px;color:#456168;background:#e9f1ee;border:1px solid rgba(49,92,73,.13);border-radius:14px;text-align:center;overflow-wrap:anywhere;
strong,span{display:block}strong{margin-bottom:4px;font-size:12px}span{font-size:12px;line-height:1.5}
button{display:block;max-width:100%;margin:10px auto 0;padding:9px 12px;color:#fff;background:#3c6b58;border:0;border-radius:999px;font-weight:750;line-height:1.25;white-space:normal;cursor:pointer}em{display:block;margin-top:7px;color:#357056;font-size:12px;font-style:normal;font-weight:750}
`;
